import { ImportInvoiceOperationResult } from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  InstalledInverter,
  PvInverter,
  Site,
} from "@inrange/building-manager-api-client/models-site";

export type OperationResult = {
  type: "success" | "error";
  message: string;
  response?: ImportInvoiceOperationResult;
};

export const formatNumber0dp = (
  value: number | string,
  useGrouping: boolean = true
): string => {
  return formatNumber(value, 0, 0, useGrouping);
};

export const formatNumber1dp = (
  value: number | string,
  useGrouping: boolean = true
): string => {
  return formatNumber(value, 1, 1, useGrouping);
};

export const formatNumber2dp = (
  value: number | string,
  useGrouping: boolean = true
): string => {
  return formatNumber(value, 2, 2, useGrouping);
};

export const formatNumber3dp = (
  value: number | string,
  useGrouping: boolean = true
): string => {
  return formatNumber(value, 3, 3, useGrouping);
};

export const formatNumber = (
  value: number | string,
  minimumNumFractionDigits: number,
  maximumFractionDigits: number,
  useGrouping: boolean = true
): string => {
  if (typeof value === "number") {
    if (value === 0) {
      // Always display negative 0 as positive 0
      // https://stackoverflow.com/questions/7223359/are-0-and-0-the-same
      value += 0;
    }
    const numberFormat = new Intl.NumberFormat("en-GB", {
      style: "decimal",
      minimumFractionDigits: minimumNumFractionDigits,
      maximumFractionDigits: maximumFractionDigits,
      //@ts-expect-error - TS doesn't know about the roundingMode option
      roundingMode: "halfCeil",
      useGrouping: useGrouping,
    });
    return numberFormat.format(value);
  }

  const parsedValue = parseFloat(value);
  if (isNaN(parsedValue)) {
    return value;
  }

  const startsWithDecimal = value.startsWith(".");
  if (startsWithDecimal) {
    return value;
  }

  const endsWithDecimal = value.endsWith(".");

  const numberFormat = new Intl.NumberFormat("en-GB", {
    style: "decimal",
    maximumFractionDigits: maximumFractionDigits,
    //@ts-expect-error - TS doesn't know about the roundingMode option
    roundingMode: "trunc",
    useGrouping: useGrouping,
  });
  const formattedValue = numberFormat.format(parsedValue);

  const originalDecimals = value.split(".")[1]?.length || 0;
  const formattedDecimals = formattedValue.split(".")[1]?.length || 0;
  if (originalDecimals > maximumFractionDigits) {
    return formattedValue;
  }

  // Pad with zeros if needed
  if (originalDecimals > formattedDecimals) {
    const [whole, fraction = ""] = formattedValue.split(".");
    const paddedFraction = fraction.padEnd(originalDecimals, "0");
    return `${whole}.${paddedFraction}`;
  }

  return endsWithDecimal ? formattedValue + "." : formattedValue;
};

export const operationStatusMessage = (
  status: string | undefined,
  options?: {
    progress?: { current: number; total: number; message: string };
    uploadProgress?: { current: number; total: number };
    generateInvoice?: boolean;
  }
): string => {
  if (options?.uploadProgress) {
    return `Uploading ${options.uploadProgress.current}/${options.uploadProgress.total}...`;
  }
  switch (status) {
    case "pending":
      return "Starting operation...";
    case "running":
      if (options?.progress) {
        return options.progress.message;
      }
      if (options?.generateInvoice) {
        return "Processing readings and generating invoice...";
      }
      return "Processing readings...";
    default:
      return "Processing...";
  }
};

export const formatDateYMD = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const epochSecondsToDate = (
  epochSeconds: string | number | undefined | null
): string => {
  if (!epochSeconds) return "";
  if (typeof epochSeconds === "string") {
    epochSeconds = parseInt(epochSeconds);
  }
  const date = new Date(epochSeconds * 1000);
  return date.toISOString().split("T")[0];
};

// Render epoch seconds as YYYY-MM-DD in the given timezone. Use this for
// timestamps written by BMA in a site-local timezone (e.g. issueDate /
// invoiceDueDate) - rendering via UTC instead would give the previous day
// for any site whose tz is east of UTC at the moment of issue.
export const epochSecondsToDateInTz = (
  epochSeconds: string | number | undefined | null,
  timeZone: string | undefined
): string => {
  if (!epochSeconds) return "";
  if (typeof epochSeconds === "string") {
    epochSeconds = parseInt(epochSeconds);
  }
  if (!timeZone) {
    return new Date(epochSeconds * 1000).toISOString().split("T")[0];
  }
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(epochSeconds * 1000));
};

export const epochSecondsToDateTime = (
  epochSeconds: string | number | undefined | null
): string => {
  if (!epochSeconds) return "";
  if (typeof epochSeconds === "string") {
    epochSeconds = parseInt(epochSeconds);
  }
  const date = new Date(epochSeconds * 1000);
  return date.toISOString().substring(0, 16);
};

type YearMonth = {
  year: number;
  month: number;
};

const isBeforeOrEqual = (left: YearMonth, right: YearMonth) => {
  return (
    left.year < right.year ||
    (left.year === right.year && left.month <= right.month)
  );
};

const incrementMonth = ({ year, month }: YearMonth): YearMonth => {
  if (month === 12) {
    return { year: year + 1, month: 1 };
  }
  return { year, month: month + 1 };
};

const formatYearMonth = ({ year, month }: YearMonth) =>
  `${year}-${String(month).padStart(2, "0")}`;

const parseYearMonthFromDateString = (dateString: string): YearMonth => {
  const [year, month] = dateString.split("-").map(Number);
  return { year, month };
};

export const computeMonthsToProcess = (
  commerciallyOperationalDate: string | undefined,
  timeZone: string | undefined
): string[] => {
  if (!commerciallyOperationalDate || !timeZone) {
    return [];
  }

  const startMonth = parseYearMonthFromDateString(commerciallyOperationalDate);
  const nowMonth = parseYearMonthFromDateString(getTodayInTimeZone(timeZone));

  const lastCompleteMonth =
    nowMonth.month === 1
      ? { year: nowMonth.year - 1, month: 12 }
      : { year: nowMonth.year, month: nowMonth.month - 1 };

  if (!isBeforeOrEqual(startMonth, lastCompleteMonth)) {
    return [];
  }

  const months: string[] = [];
  let cursor = startMonth;

  while (isBeforeOrEqual(cursor, lastCompleteMonth)) {
    months.push(formatYearMonth(cursor));
    cursor = incrementMonth(cursor);
  }

  return months;
};

export const getTodayInTimeZone = (timeZone: string): string => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    // 'en-CA' gives YYYY-MM-DD
    timeZone, // This is what determines which timezone's "today" you get
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
};

export const currencyValue = (value: number, currencyCode: string): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const setMissingValuesZeroAndParseStringsToNumbers = (
  objA: object,
  objB: object
) => {
  for (const key in objA) {
    if (objB[key] === undefined) {
      objB[key] = 0;
    }
  }
  for (const key in objA) {
    objA[key] = parseAsNumber(objA[key]);
  }
  for (const key in objB) {
    if (objA[key] === undefined) {
      objA[key] = 0;
    }
  }
  for (const key in objB) {
    objB[key] = parseAsNumber(objB[key]);
  }
};

export const parseAsNumber = (value: string | number | undefined): number => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    return parseFloat(value);
  }
  return 0;
};

export const roundToDp = (value: number, dp: number): number => {
  return Math.round(value * Math.pow(10, dp)) / Math.pow(10, dp);
};

export const inverterRatedOutputPowerTotalKwpAc = (
  inverterSpecs: Record<string, PvInverter>,
  installedInverters: AllowStringForNumbers<InstalledInverter>[]
) => {
  return (
    installedInverters.reduce((total, installedInverter) => {
      const inverterSpec = inverterSpecs[installedInverter.name];
      if (inverterSpec) {
        return (
          total +
          (inverterSpec.ratedAcOutputPower || 0) *
            parseAsNumber(installedInverter.count)
        );
      }
      return total;
    }, 0) / 1000.0
  );
};

export const inverterRatedAcOutputPowerMaxRatio = (
  inverterSpecs: Record<string, PvInverter>,
  installedInverters: AllowStringForNumbers<InstalledInverter>[]
) => {
  const totalKwpAc = inverterRatedOutputPowerTotalKwpAc(
    inverterSpecs,
    installedInverters
  );
  const totalMaxKwDc =
    installedInverters.reduce((total, installedInverter) => {
      const inverterSpec = inverterSpecs[installedInverter.name];
      if (inverterSpec) {
        return (
          total +
          (inverterSpec.maxDcPower || 0) *
            parseAsNumber(installedInverter.count)
        );
      }
      return total;
    }, 0) / 1000.0;
  return totalKwpAc > 0 ? totalMaxKwDc / totalKwpAc : 0;
};

export const isLinkedToNonTestOrg = (site: Site) => {
  return site.siteOwnerships.some((ownership) => !ownership.isTest);
};

export const isLinkedToNonTestOrgWithNameCheck = (site: Site) => {
  return site.siteOwnerships.some((ownership) => {
    if (ownership.isTest) {
      return false;
    }
    const name = (ownership.name ?? "").toLowerCase();
    return !name.includes("inrange");
  });
};

export const isProdSiteBasedOnName = (site: Site) => {
  const name = site.name.toLowerCase();
  return !name.startsWith("demo:");
};
